import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function FirstDibsCalloutBanner() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Submit to form endpoint
      const body = new URLSearchParams({ email });
      await fetch('https://readdy.ai/api/form/d6nomdo97e9r3849v2e0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      // Send confirmation email via Edge Function (non-blocking)
      fetch('https://yshatcivfpnzxcgppout.supabase.co/functions/v1/first-dibs-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }).catch((err) => {
        console.error('Confirmation email error:', err);
        // Don't block the user experience if email fails
      });

      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section 
      id="first-dibs-signup"
      className="bg-gradient-to-br from-[#1C2B1E] via-[#1A2540] to-[#1C2B1E] py-12 md:py-16"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E8B84B]/20 to-[#D4A843]/20 border border-[#E8B84B]/30 rounded-full">
              <i className="ri-mail-star-line text-[#E8B84B] text-lg"></i>
              <span className="text-[#E8B84B] font-semibold text-sm tracking-wide">
                First Dibs — Email Exclusive
              </span>
            </div>
          </div>

          {/* Main Content */}
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Best Cars Are Gone{' '}
              <span className="text-[#E8B84B]">Before You Even See Them.</span>
            </h2>
            <p className="text-gray-300 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              Get 24–48 hour early access to new arrivals — before they're cleaned, photographed, or posted online.
              Drop your email and we'll alert you first.
            </p>
          </div>

          {/* Email Form */}
          <div className="max-w-xl mx-auto mb-8">
            {submitted ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8B84B]/20 rounded-full">
                  <i className="ri-check-line text-[#E8B84B] text-2xl"></i>
                </div>
                <p className="text-white font-semibold text-lg">You're on the list!</p>
                <p className="text-gray-400 text-sm text-center">Check your email — we just sent you a confirmation with all the details.</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="first-dibs-email-form"
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-[#E8B84B]/60 transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#E8B84B] to-[#D4A843] text-[#1A2540] font-bold rounded-lg hover:shadow-lg hover:shadow-[#E8B84B]/20 transition-all duration-300 whitespace-nowrap disabled:opacity-60"
                >
                  <i className="ri-vip-crown-fill text-base"></i>
                  <span>{loading ? 'Submitting…' : 'Get First Dibs'}</span>
                </button>
              </form>
            )}
            {error && <p className="text-red-400 text-sm mt-2 text-center">{error}</p>}
            <p className="text-gray-500 text-xs text-center mt-3">No spam. Unsubscribe anytime.</p>
          </div>

          {/* Secondary CTA */}
          <div className="flex justify-center mb-8">
            <button
              onClick={handleScrollToTop}
              className="inline-flex items-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-lg border border-white/20 hover:bg-white/20 transition-all duration-300 whitespace-nowrap"
            >
              <span>See What's Live Now</span>
              <i className="ri-arrow-up-line text-lg"></i>
            </button>
          </div>

          {/* Feature Pills */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
              <i className="ri-time-line text-[#E8B84B]"></i>
              <span className="text-gray-300 text-sm">24–48 hr head start</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
              <i className="ri-mail-line text-[#E8B84B]"></i>
              <span className="text-gray-300 text-sm">Email alerts only</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
              <i className="ri-filter-3-line text-[#E8B84B]"></i>
              <span className="text-gray-300 text-sm">Before it goes public</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}